import { useEffect, useRef, useState } from 'react';
import { Button, Callout, Flex, TextArea } from '@radix-ui/themes';
import { SendHorizonal, Square } from 'lucide-react';
import type { LinkState } from '@mac-remote/protocol';
import { getTelegramWebApp } from '../lib/telegram';

const DRAFT_KEY = 'mac-remote:draft';

interface Draft {
  sid: string;
  text: string;
}

/**
 * Input, and the rules about when input is allowed.
 *
 * Controlled from above so the snippet bar can see the draft and history can fill it.
 *
 * Keystrokes are never queued for an offline Mac. A key typed against a terminal you
 * cannot see would land later in an unknown context — a stray `y\n` could confirm
 * something you never read. The invariant is that the server never writes into a PTY
 * on its own. The one concession is the draft below: it is held client-side and only
 * sent after an explicit confirmation once the Mac is back.
 */
export function Composer({
  sid,
  link,
  busy,
  value,
  onValueChange,
  onSend,
  onInterrupt,
}: {
  sid: string;
  link: LinkState;
  busy: boolean;
  value: string;
  onValueChange: (text: string) => void;
  onSend: (text: string) => void;
  onInterrupt: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const online = link === 'online';
  const askedRef = useRef(false);

  useEffect(() => {
    if (online || !value.trim()) return;
    const draft: Draft = { sid, text: value };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [online, value, sid]);

  useEffect(() => {
    if (!online || askedRef.current) return;
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return;

    let draft: Draft;
    try {
      draft = JSON.parse(raw) as Draft;
    } catch {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }
    if (draft.sid !== sid || !draft.text.trim()) return;

    askedRef.current = true;
    const wa = getTelegramWebApp();
    const question = `Отправить в ${sid}?\n\n${draft.text.slice(0, 120)}`;
    const commit = (yes: boolean) => {
      localStorage.removeItem(DRAFT_KEY);
      if (yes) {
        onSend(draft.text);
        onValueChange('');
      }
    };
    if (wa?.showConfirm) wa.showConfirm(question, commit);
    else commit(window.confirm(question));
  }, [online, sid, onSend, onValueChange]);

  const send = () => {
    if (!online || !value.trim()) return;
    onSend(value);
    onValueChange('');
    localStorage.removeItem(DRAFT_KEY);
    getTelegramWebApp()?.HapticFeedback?.impactOccurred('light');
  };

  return (
    <Flex direction="column" gap="2" px="2" pb="2" pt="1" style={{ background: 'var(--app-secondary-bg)' }}>
      {!online && (
        <Callout.Root color="amber" size="1">
          <Callout.Text>
            {link === 'stale' ? 'Мак не отвечает' : 'Мак офлайн'}. Ввод отключён — команда ушла бы
            вслепую. Черновик сохранится.
          </Callout.Text>
        </Callout.Root>
      )}
      <TextArea
        placeholder={busy ? 'Codex работает — можно писать дальше' : 'Что сделать в этом проекте?'}
        value={value}
        // Collapses to one line while Codex works: that is when you want to watch
        // output, not type. Touching it expands it again.
        rows={focused || value ? 3 : busy ? 1 : 2}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends, Shift+Enter is a newline — the contract every messenger uses.
          // On a phone the return key is right there; reaching for a separate button
          // to send every message was the original complaint.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        style={{ fontSize: 16 }} // < 16px makes iOS Safari zoom the whole viewport on focus
      />
      <Flex gap="2">
        {busy && (
          // Stop is only meaningful while something is running, so it only exists then.
          <Button size="2" variant="soft" color="red" disabled={!online} onClick={onInterrupt}>
            <Square size={13} /> Стоп
          </Button>
        )}
        <Button size="2" style={{ flex: 1 }} disabled={!online || !value.trim()} onClick={send}>
          <SendHorizonal size={15} /> Отправить
        </Button>
      </Flex>
    </Flex>
  );
}
